import { Component, Input, OnInit } from '@angular/core';
import { SharedModule } from '@/app/shared/shared.module';
import { CropHleathMapTypeKey } from '../../crop-health/crop-health-map/crop-health-map-footer/crop-health-map-footer.component';

interface LegendItem {
  color: string;
  title: string;
  description: string;
}

interface LegendTextItem {
  title: string;
  description: string;
}

type DistributionLegendMapTypeKey = Exclude<CropHleathMapTypeKey, 'risk'>;

@Component({
  selector: 'app-ndvi-ndmi-distribution-color-legend-modal',
  imports: [SharedModule],
  templateUrl: './ndvi-ndmi-distribution-color-legend-modal.component.html',
  styleUrl: './ndvi-ndmi-distribution-color-legend-modal.component.less',
})
export class NdviNdmiDistributionColorLegendModalComponent implements OnInit {
  private static readonly I18N = 'doktar_app.services.crop_monitoring';

  private static readonly DESCRIPTION_KEYS: Record<DistributionLegendMapTypeKey, string> = {
    ndvi: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.ndvi_distribution_color_legend_description`,
    ndmi: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.ndmi_distribution_color_legend_description`,
    inspection: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.inspection_distribution_color_legend_description`,
  };

  private static readonly LEGEND_TEXT_CONFIG: Record<DistributionLegendMapTypeKey, LegendTextItem[]> = {
    ndvi: [
      {
        title: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.distributuon_color_legend_no_vegetation_cover_title`,
        description: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.distributuon_color_legend_no_vegetation_cover_description`,
      },
      {
        title: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.unhealthy_vegetation_title`,
        description: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.unhealthy_vegetation_description`,
      },
      {
        title: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.optimal_crop_conditions_title`,
        description: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.optimal_crop_conditions_description`,
      },
      {
        title: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.healty_vegetation_title`,
        description: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.optimal_crop_conditions_description`,
      },
    ],
    ndmi: [
      {
        title: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.no_plant_title`,
        description: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.no_plant_description`,
      },
      {
        title: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.low_plant_water_retention_title`,
        description: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.low_plant_water_retention_description`,
      },
      {
        title: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.medium_plant_water_retention_title`,
        description: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.medium_plant_water_retention_description`,
      },
      {
        title: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.high_plant_water_retention_title`,
        description: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.high_plant_water_retention_description`,
      },
    ],
    inspection: [
      {
        title: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.below_average_growth_title`,
        description: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.below_average_growth_description`,
      },
      {
        title: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.average_growth_title`,
        description: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.average_growth_description`,
      },
      {
        title: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.above_average_growth_title`,
        description: `${NdviNdmiDistributionColorLegendModalComponent.I18N}.above_average_growth_description`,
      },
    ],
  };

  @Input() mapTypeKey: CropHleathMapTypeKey = 'ndvi';
  @Input() legendColors: string[] = [];

  legends: LegendItem[] = [];

  ngOnInit(): void {
    this.setLegends();
  }

  get descriptionKey(): string {
    const key = this.resolveLegendMapTypeKey();
    return NdviNdmiDistributionColorLegendModalComponent.DESCRIPTION_KEYS[key];
  }

  get showNdmiGradient(): boolean {
    return this.mapTypeKey === 'ndmi';
  }

  isLightSwatch(color: string): boolean {
    const normalized = color?.trim().toLowerCase();
    if (!normalized) {
      return false;
    }

    if (normalized === '#fff' || normalized === '#ffffff') {
      return true;
    }

    const hex = normalized.replace('#', '');
    if (hex.length !== 6) {
      return false;
    }

    const red = Number.parseInt(hex.slice(0, 2), 16);
    const green = Number.parseInt(hex.slice(2, 4), 16);
    const blue = Number.parseInt(hex.slice(4, 6), 16);

    return (red + green + blue) / 3 > 240;
  }

  private resolveLegendMapTypeKey(): DistributionLegendMapTypeKey {
    return this.mapTypeKey === 'risk' ? 'ndvi' : this.mapTypeKey;
  }

  private setLegends(): void {
    const key = this.resolveLegendMapTypeKey();
    this.legends = NdviNdmiDistributionColorLegendModalComponent.LEGEND_TEXT_CONFIG[key].map((item, index) => ({
      ...item,
      color: this.legendColors[index] ?? '',
    }));
  }
}
